import { useEffect, useRef } from 'react';
import { Building2 } from 'lucide-react';
import Store from 'assets/img/store/Store.svg';
import { STORE } from 'state/store/type';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from 'components/ui/command';

export interface StoreCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storesList: STORE.IStore[] | null;
  accessibleStoresCount: number;
  onStoreSelect: (store: STORE.IStore) => void;
  onOrgViewSelect: () => void;
}

export const StoreCommandPalette = ({
  open,
  onOpenChange,
  storesList,
  accessibleStoresCount,
  onStoreSelect,
  onOrgViewSelect,
}: StoreCommandPaletteProps) => {
  // Use ref to track open state to avoid re-registering event listener
  const openRef = useRef(open);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Cmd+K / Ctrl+K keyboard shortcut to open command palette
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!openRef.current);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onOpenChange]);

  const handleStoreSelect = (store: STORE.IStore) => {
    onStoreSelect(store);
    onOpenChange(false);
  };

  const handleOrgViewSelect = () => {
    onOrgViewSelect();
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search stores or go to org view..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Quick Actions">
          <CommandItem
            value="org view organization"
            onSelect={handleOrgViewSelect}
          >
            <Building2 className="mr-2 h-4 w-4" />
            <span>Org view</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {accessibleStoresCount}{' '}
              {accessibleStoresCount === 1 ? 'store' : 'stores'}
            </span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Stores">
          {storesList &&
            storesList.map((store) => (
              <CommandItem
                key={store.organization_id}
                value={store.dba_name}
                onSelect={() => handleStoreSelect(store)}
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <img src={Store} alt="" aria-hidden="true" />
                </div>
                <span>{store.dba_name}</span>
                {!store.has_access && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    No access
                  </span>
                )}
              </CommandItem>
            ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
